import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import ErrorText from "../../components/errorText";
import logger, { Logger } from '../../utils/logger';

// Basic types for conditions
export interface AutomationCondition {
  id: string;
  fieldId: string;
  operator: string;
  value: string;
  logicOperator?: 'AND' | 'OR';
  customFieldValue?: string; 
}

export interface AutomationConditionBlockProps {
  onChange: (conditions: AutomationCondition[]) => void;
  initialConditions?: AutomationCondition[];
  isVisible: boolean;
  isLoading?: boolean;
  title?: string;
  description?: string;
  stepNumber?: number;
  eventType?: string;
}

// Operator mapping for each field type - only using supported operators

const FIELD_OPERATORS: Record<string, {value: string, label: string}[]> = {
  name: [
    { value: 'equals', label: 'equal to' },
    { value: 'not_equals', label: 'not equal to' },
    { value: 'contains', label: 'contains' },           // NEW: Added contains
    { value: 'not_contains', label: 'does not contain' } // NEW: Added not contains
  ],
  email: [
    { value: 'equals', label: 'equal to' },
    { value: 'not_equals', label: 'not equal to' },
    { value: 'contains', label: 'contains' },           // NEW: Added contains
    { value: 'not_contains', label: 'does not contain' } // NEW: Added not contains
  ],
  phone: [
    { value: 'equals', label: 'equal to' },
    { value: 'not_equals', label: 'not equal to' },
    { value: 'contains', label: 'contains' },           // NEW: Added contains
    { value: 'not_contains', label: 'does not contain' } // NEW: Added not contains
  ],
  amount: [
    { value: 'equals', label: 'equal to' },
    { value: 'not_equals', label: 'not equal to' },
    { value: 'greater_than', label: 'greater than' },
    { value: 'greater_than_equal', label: 'greater than or equal to' },
    { value: 'less_than', label: 'less than' },
    { value: 'less_than_equal', label: 'less than or equal to' }
    // NOTE: No contains for amount fields - doesn't make logical sense
  ],
  text: [
    { value: 'equals', label: 'equal to' },
    { value: 'not_equals', label: 'not equal to' },
    { value: 'contains', label: 'contains' },           // NEW: Added contains
    { value: 'not_contains', label: 'does not contain' } // NEW: Added not contains
  ],
  number: [
    { value: 'equals', label: 'equal to' },
    { value: 'not_equals', label: 'not equal to' },
    { value: 'greater_than', label: 'greater than' },
    { value: 'greater_than_equal', label: 'greater than or equal to' },
    { value: 'less_than', label: 'less than' },
    { value: 'less_than_equal', label: 'less than or equal to' }
    // NOTE: No contains for number fields - doesn't make logical sense
  ],
  selection: [
    { value: 'equals', label: 'equal to' },
    { value: 'not_equals', label: 'not equal to' },
    { value: 'contains', label: 'contains' },           // NEW: Added contains for partial matches
    { value: 'not_contains', label: 'does not contain' } // NEW: Added not contains
  ],
  'multi-selection': [
    { value: 'equals', label: 'equal to' },
    { value: 'not_equals', label: 'not equal to' },
    { value: 'contains', label: 'contains' },           // NEW: Added contains - useful for checking if multi-selection contains specific value
    { value: 'not_contains', label: 'does not contain' } // NEW: Added not contains
  ]
};

/**
 * AutomationConditionBlock component - Handles field-level conditions for automation
 */
const AutomationConditionBlock: React.FC<AutomationConditionBlockProps> = ({
  onChange,
  initialConditions = [],
  isVisible,
  isLoading = false,
  title = "Field Criteria",
  description = "Define conditions for lead filtering",
  stepNumber = 2,
  eventType
}) => {
    const initializedRef = useRef(false);
  // State for storing conditions
  const [conditions, setConditions] = useState<AutomationCondition[]>([]);
  
  const conditionsWereCleared = useRef(false);
  const prevEventTypeRef = useRef(eventType);


  // Ref to track previous conditions for comparison
  const prevConditionsRef = useRef<string>("");

  // Get field data from Redux store and filter out location, date, and time fields
  const customFormFields = useSelector((state: any) => {
    const fields = state?.rootReducers?.user?.userPreferences?.customForm || [];
    // Filter out location, date, and time type fields
    return fields.filter((field: any) => 
      field.type !== 'location' && 
      field.type !== 'date' && 
      field.type !== 'time'
    );
  });

  // Set conditions from props when they change
  useEffect(() => {
    // Skip if nothing meaningful changed
    if (prevEventTypeRef.current === eventType && initializedRef.current) {
      logger.log('[AutomationConditionBlock] Skipping - no meaningful changes detected');
      return;
    }
    
    logger.log('[AutomationConditionBlock] Initializing effect triggered', {
      prevEventType: prevEventTypeRef.current,
      currentEventType: eventType,
      initializedRef: initializedRef.current,
      conditionsLength: conditions.length,
      hasInitialConditions: initialConditions && initialConditions.length > 0
    });
    
    // Update the previous event type
    prevEventTypeRef.current = eventType;
    
    // If we have initialConditions, use them
    if (initialConditions && initialConditions.length > 0) {
      Logger.log('[AutomationConditionBlock] Using provided initial conditions');
      setConditions([...initialConditions]);
      initializedRef.current = true;
      return;
    }
    
    // If we already have conditions for this event type, don't recreate them
    if (conditions.length > 0 && initializedRef.current) {
      logger.log('[AutomationConditionBlock] Already have conditions, not recreating');
      return;
    }
    
    Logger.log('[AutomationConditionBlock] Initializing with eventType:', eventType);
    logger.log('[AutomationConditionBlock] Creating default conditions for event type:', eventType);
    
    // Otherwise set defaults based on event type
    if (eventType === 'create_quotation' || eventType === 'create_invoice' || eventType === 'create_order') {
      Logger.log('[AutomationConditionBlock] Setting default total field condition');
      setConditions([{
        id: generateId(),
        fieldId: 'total',
        operator: 'greater_than',
        value: '',
        logicOperator: undefined
      }]);
    } 
    else if (eventType === 'edit_quotation' || eventType === 'edit_invoice' || eventType === 'edit_order') {
      Logger.log('[AutomationConditionBlock] Setting default status field condition');
      setConditions([{
        id: generateId(),
        fieldId: 'status',
        operator: 'equals',
        value: '',
        logicOperator: undefined
      }]);
    }
    // Call duration
    else if (eventType === 'call_duration') {
      Logger.log('[AutomationConditionBlock] Setting default call duration condition');
      setConditions([{
        id: generateId(),
        fieldId: 'extraDetails.duration',
        operator: 'greater_than',
        value: '',
        logicOperator: undefined
      }]);
    }
    // Call count
    else if (eventType === 'call_count') {
      Logger.log('[AutomationConditionBlock] Setting default call count condition');
      setConditions([{
        id: generateId(),
        fieldId: 'count',
        operator: 'greater_than',
        value: '',
        logicOperator: undefined
      }]);
    }
    // Lead events
    else if (eventType === 'edit_lead' || eventType === 'create_lead' || eventType === 'new_lead_added') {
      logger.log('[AutomationConditionBlock] Setting default condition for lead event:', eventType);
      // Set a default field for lead events if there are custom form fields
      if (customFormFields && customFormFields.length > 0) {
        const defaultField = customFormFields[0];
        setConditions([{
          id: generateId(),
          fieldId: defaultField.value || '',
          operator: 'equals',
          value: '',
          logicOperator: undefined
        }]);
      }
    }
    
    initializedRef.current = true;
  }, [eventType, initialConditions, customFormFields, conditions.length]);
  
  // Add this useEffect to handle clearing conditions properly
  useEffect(() => {
    // This will run when conditions are updated
    if (conditions.length === 0 && initializedRef.current) {
      logger.log('[AutomationConditionBlock] Conditions were cleared, notifying parent');
      onChange([]);
    }
  }, [conditions, onChange]);

  // Update parent when conditions change
// Add this before useEffect that updates parent
useEffect(() => {
    const currentConditionsStr = JSON.stringify(conditions);
    
    // Only notify parent if conditions actually changed and all values are filled
    if (currentConditionsStr !== prevConditionsRef.current) {
      // Check if all conditions have values before sending to parent
      const allConditionsFilled = conditions.every(condition => 
        condition.fieldId && condition.operator && condition.value !== '');
      
      if (allConditionsFilled) {
        onChange(conditions);
        prevConditionsRef.current = currentConditionsStr;
      } else {
        // Don't update parent but still update reference to prevent re-renders
        prevConditionsRef.current = currentConditionsStr;
      }
    }
  }, [conditions, onChange]);

  // Generate a unique ID for new conditions
  const generateId = () => `condition-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

// Add this function to the component
const getContextualHelpText = () => {
  if (eventType === 'create_quotation' || eventType === 'create_invoice' || eventType === 'create_order') {
    const documentType = eventType.split('_')[1];
    return (
      <>
        <strong>Example:</strong> Where <i>Total</i> is <i>greater than</i> "5000"
        <br />
        <strong>Purpose:</strong> Automatically identify high-value {documentType}s for special handling. Use this to prioritize important sales or flag big-ticket items.
      </>
    );
  } else if (eventType === 'edit_quotation' || eventType === 'edit_invoice' || eventType === 'edit_order') {
    const documentType = eventType.split('_')[1];
    let statusExample = documentType === 'quotation' ? "Approved" : 
                        documentType === 'invoice' ? "Paid" : "Shipped";
    
    return (
      <>
        <strong>Example:</strong> Where <i>Status</i> is <i>equal to</i> "{statusExample}"
        <br />
        <strong>Purpose:</strong> Trigger actions when a {documentType}'s status changes. Perfect for follow-ups when quotes are approved, orders are shipped, or invoices are paid.
      </>
    );
  } else if (eventType === 'call_duration') {
    return (
      <>
        <strong>Example:</strong> Where <i>Duration</i> is <i>greater than</i> "60" seconds
        <br />
        <strong>Purpose:</strong> Identify when you've successfully connected with a lead. Great for sending follow-up messages after meaningful conversations or moving leads to a different stage after successful calls.
      </>
    );
  } else if (eventType === 'call_count') {
    return (
      <>
        <strong>Example:</strong> Where <i>Count</i> is <i>greater than</i> "5"
        <br />
        <strong>Purpose:</strong> Track engagement based on total number of calls (incoming and outgoing). Use this to identify highly engaged leads who you've called multiple times or who have called you frequently.
      </>
    );
  } else if (eventType === 'new_lead_added' || eventType === 'edit_lead') {
    return (
      <>
        <strong>Examples:</strong>
        <br />
        • Where <i>Company Name</i> is <i>equal to</i> "Tech Corp"
        <br />
        • Where <i>Email</i> <i>contains</i> "@gmail.com" {/* NEW: Added contains example */}
        <br />
        • Where <i>Notes</i> <i>does not contain</i> "unqualified" {/* NEW: Added not contains example */}
        <br />
        <strong>Note:</strong> Values are case sensitive. "High Priority" is different from "high priority".
        <br />
        <strong>Contains Usage:</strong> Use "contains" to find partial matches like email domains, keywords in text fields, or partial company names.
      </>
    );
  }
  
  // Default case - updated with contains examples
  return (
    <>
      <strong>Examples:</strong>
      <br />
      • Where <i>Field</i> is <i>equal to</i> "Example Value"
      <br />
      • Where <i>Text Field</i> <i>contains</i> "keyword" {/* NEW: Added contains example */}
      <br />
      • Where <i>Description</i> <i>does not contain</i> "excluded" {/* NEW: Added not contains example */}
      <br />
      <strong>Note:</strong> Specify conditions to make your automation more targeted.
    </>
  );
};

// Add a new condition
const addCondition = () => {
    // For invoice/quotation/order creation events - use total field
    if (eventType === 'create_quotation' || eventType === 'create_invoice' || eventType === 'create_order') {
      const newCondition: AutomationCondition = {
        id: generateId(),
        fieldId: 'total',
        operator: 'greater_than',
        value: '',
        logicOperator: 'AND'
      };
      setConditions(prevConditions => [...prevConditions, newCondition]);
      return;
    }
  
    // For invoice/quotation/order edit events - use status field
    if (eventType === 'edit_quotation' || eventType === 'edit_invoice' || eventType === 'edit_order') {
      const newCondition: AutomationCondition = {
        id: generateId(),
        fieldId: 'status',
        operator: 'equals',
        value: '',
        logicOperator: 'AND'
      };
      setConditions(prevConditions => [...prevConditions, newCondition]);
      return;
    }
  
    // For call events - keep original logic
    if (eventType === 'call_duration') {
      const newCondition: AutomationCondition = {
        id: generateId(),
        fieldId: 'extraDetails.duration',
        operator: 'greater_than',
        value: '',
        logicOperator: 'AND'
      };
      setConditions(prevConditions => [...prevConditions, newCondition]);
      return;
    }
  
    if (eventType === 'call_count') {
      const newCondition: AutomationCondition = {
        id: generateId(),
        fieldId: 'count',
        operator: 'greater_than',
        value: '',
        logicOperator: 'AND'
      };
      setConditions(prevConditions => [...prevConditions, newCondition]);
      return;
    }
    
    // Default case for other event types
    const defaultField = customFormFields[0]?.value || '';
    const defaultType = customFormFields[0]?.type || 'text';
    const defaultOperator = FIELD_OPERATORS[defaultType]?.[0]?.value || 'equals';
  
    const newCondition: AutomationCondition = {
      id: generateId(),
      fieldId: defaultField,
      operator: defaultOperator,
      value: '',
      logicOperator: 'AND'
    };
  
    setConditions(prevConditions => [...prevConditions, newCondition]);
  };


  const isSingleConditionEvent = (eventType?: string): boolean => {
    // List of events that should have exactly one condition
    const singleConditionEvents = [
      'create_quotation', 
      'create_invoice', 
      'create_order',
      'edit_quotation',
      'edit_invoice', 
      'edit_order',
      'call_duration',
      'call_count'
    ];
    
    return eventType ? singleConditionEvents.includes(eventType) : false;
  };

 const removeCondition = (id: string) => {
  // Allow removing conditions for all event types by removing the restriction
  setConditions(prevConditions => {
    // Get the new state after removal
    const newConditions = prevConditions.filter(condition => condition.id !== id);
    
    // If this results in empty conditions, notify parent component via onChange
    if (newConditions.length === 0) {
      conditionsWereCleared.current = true; // Set the flag when all conditions are removed
      logger.log('[AutomationConditionBlock] All conditions were cleared by user');
      onChange([]);
    }
    
    return newConditions;
  });
};

  // Update a specific field in a condition
  const updateCondition = (id: string, field: keyof AutomationCondition, value: string) => {
    setConditions(prevConditions => prevConditions.map(condition => {
      if (condition.id === id) {
        // Special handling when field changes - reset operator based on new field type
        if (field === 'fieldId') {
          const fieldObj = customFormFields.find((f: any) => f.value === value);
          const fieldType = fieldObj?.type || 'text';
          const defaultOperator = FIELD_OPERATORS[fieldType]?.[0]?.value || 'equals';
          
          return {
            ...condition,
            fieldId: value,
            operator: defaultOperator,
            value: '' // Reset value when field changes
          };
        }
        
        return { ...condition, [field]: value };
      }
      return condition;
    }));
  };

  // Update the logic operator (AND/OR)
  const updateLogicOperator = (id: string, value: 'AND' | 'OR') => {
    setConditions(prevConditions => prevConditions.map(condition => {
      if (condition.id === id) {
        return { ...condition, logicOperator: value };
      }
      return condition;
    }));
  };

  // Get field type from field ID
  const getFieldType = (fieldId: string) => {
    const field = customFormFields.find((f: any) => f.value === fieldId);
    return field?.type || 'text';
  };

  // Render operators dropdown based on field type

// Enhanced renderOperators function with better logging and contains support
const renderOperators = (fieldId: string) => {
  // Console log for debugging which operators are being rendered
  logger.log('[AutomationConditionBlock] Rendering operators for fieldId:', fieldId);
  
  // For custom field entries, show all operators including contains
  if (fieldId === 'custom_field_entry') {
    const allOperators: {value: string, label: string}[] = [];
    
    Object.values(FIELD_OPERATORS).forEach(operators => {
      operators.forEach(op => {
        if (!allOperators.some(existing => existing.value === op.value)) {
          allOperators.push(op);
        }
      });
    });
    
    logger.log('[AutomationConditionBlock] Custom field operators:', allOperators);
    
    return allOperators.map((op, index) => (
      <option key={index} value={op.value}>{op.label}</option>
    ));
  }
  
  // For creation events, only allow greater than operators (no contains needed)
  if ((eventType === 'create_quotation' || eventType === 'create_invoice' || 
      eventType === 'create_order') && fieldId === 'total') {
    const creationOperators = [
      { value: 'greater_than', label: 'greater than' },
      { value: 'greater_than_equal', label: 'greater than or equal to' }
    ];
    
    logger.log('[AutomationConditionBlock] Creation event operators:', creationOperators);
    
    return creationOperators.map((op, index) => (
      <option key={index} value={op.value}>{op.label}</option>
    ));
  }
  
  // For call events, use number operators (no contains needed)
  if ((eventType === 'call_duration' && fieldId === 'extraDetails.duration') || 
      (eventType === 'call_count' && fieldId === 'count')) {
    
    logger.log('[AutomationConditionBlock] Call event operators:', FIELD_OPERATORS['number']);
    
    return FIELD_OPERATORS['number'].map((op, index) => (
      <option key={index} value={op.value}>{op.label}</option>
    ));
  }
  
  // For edit events, only allow equals operator (no contains needed)
  if ((eventType === 'edit_quotation' || eventType === 'edit_invoice' || 
      eventType === 'edit_order') && fieldId === 'status') {
    const editOperators = [
      { value: 'equals', label: 'equal to' }
    ];
    
    logger.log('[AutomationConditionBlock] Edit event operators:', editOperators);
    
    return editOperators.map((op, index) => (
      <option key={index} value={op.value}>{op.label}</option>
    ));
  }
  
  // Default case - determine by field type (THIS WILL NOW INCLUDE CONTAINS)
  const fieldType = getFieldType(fieldId);
  const operators = FIELD_OPERATORS[fieldType] || FIELD_OPERATORS.text;
  
  logger.log('[AutomationConditionBlock] Default operators for field type', fieldType, ':', operators);
  
  return operators.map((op, index) => (
    <option key={index} value={op.value}>{op.label}</option>
  ));
};

 // Render appropriate value input based on field type and operator
const renderValueInput = (condition: AutomationCondition) => {
    const { fieldId, operator, value } = condition;
    
    // Handler for value changes
    const handleValueChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      updateCondition(condition.id, 'value', e.target.value);
    };
    
    // For creation events with total field
    if ((eventType === 'create_quotation' || eventType === 'create_invoice' || 
        eventType === 'create_order') && fieldId === 'total') {
      return (
        <input
          type="number"
          className="form-control"
          placeholder="Enter amount..."
          value={value}
          onChange={handleValueChange}
          disabled={isLoading}
          min="0"
        />
      );
    }
    
    // For edit events with status field
    if ((eventType === 'edit_quotation' || eventType === 'edit_invoice' || 
        eventType === 'edit_order') && fieldId === 'status') {
      
      // Get status options from localStorage
      let statusOptions: {color: string, name: string, value: string, isDefault?: boolean}[] = [];
      
      try {
        const userPrefs = localStorage.getItem("user_preferences");
        if (userPrefs) {
          const preferences = JSON.parse(userPrefs);
          
          if (eventType === 'edit_quotation' && preferences?.quotationCustomization?.quotationStatus) {
            statusOptions = preferences.quotationCustomization.quotationStatus;
          } else if (eventType === 'edit_invoice' && preferences?.invoiceCustomization?.invoiceStatus) {
            statusOptions = preferences.invoiceCustomization.invoiceStatus;
          } else if (eventType === 'edit_order' && preferences?.orderCustomization?.orderStatus) {
            statusOptions = preferences.orderCustomization.orderStatus;
          }
        }
      } catch (error) {
        console.error('Error loading status options from localStorage:', error);
      }
      
      return (
        <select
          className="form-select"
          value={value}
          onChange={handleValueChange}
          disabled={isLoading}
        >
            
          <option value="">Select Status</option>
          {statusOptions && statusOptions.length > 0 ? (
            statusOptions.map((option, index) => (
              <option key={index} value={option.value}>
                {option.name || option.value}
              </option>
            ))
          ) : (
            <option value="" disabled>No status options available</option>
          )}
        </select>
      );
    }
    
    // For call events
    if ((eventType === 'call_duration' && fieldId === 'extraDetails.duration') ||
        (eventType === 'call_count' && fieldId === 'count')) {
      return (
        <input
          type="number"
          className="form-control"
          placeholder={`Enter ${eventType === 'call_duration' ? 'seconds' : 'value'}...`}
          value={value}
          onChange={handleValueChange}
          disabled={isLoading}
          min="0"
        />
      );
    }
    
    // For fields with options (selection, multi-selection)
    const fieldObj = customFormFields.find((f: any) => f.value === fieldId);
    const fieldType = fieldObj?.type || 'text';
    
    if ((fieldType === 'selection' || fieldType === 'multi-selection') && fieldObj?.options) {
      return (
        <select
          className="form-select"
          value={value}
          onChange={handleValueChange}
          disabled={isLoading}
        >
          <option value="">Select</option>
          {fieldObj.options.map((option: string, index: number) => (
            <option key={index} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    }
    
    // For date fields
    if (fieldType === 'date') {
      return (
        <input
          type="date"
          className="form-control"
          value={value}
          onChange={handleValueChange}
          disabled={isLoading}
        />
      );
    }
    
    // For number fields
    if (fieldType === 'number' || fieldType === 'amount') {
      return (
        <input
          type="number"
          className="form-control"
          placeholder="Enter value..."
          value={value}
          onChange={handleValueChange}
          disabled={isLoading}
        />
      );
    }
    
    // Default to text input
    return (
      <input
        type="text"
        className="form-control"
        placeholder="Enter value..."
        value={value}
        onChange={handleValueChange}
        disabled={isLoading}
      />
    );
  };
  
  // Only render content if visible
  if (!isVisible) {
    return null;
  }

  return (
    <div className="col-md-12 condition-block">
      <span className="auto_dot">
        <label className="dot_no">{stepNumber}</label>
      </span>
      <label className="when_then_label">{title}</label>
      <span className="when_then_trigger">
        {description}
      </span>
      
      {/* Add contextual condition example and note */}
<div className="mt-2 mb-3 p-2 bg-light rounded">
  <small className="text-muted">
    {getContextualHelpText()}
  </small>
</div>
      
      {/* Condition Rows */}
      {conditions.map((condition, index) => (
        <div key={condition.id} className="col-md-12 mt-3">
          <div className="row align-items-center">
          {/* Field Selector */}
        
{/* Field Selector */}
{/* Field Selector */}
<div className="col-md-3">
  {condition.fieldId === 'custom_field_entry' ? (
    // Show text input when custom field option is selected
    <input
      type="text"
      className="form-control"
      value={condition.customFieldValue || ''}
      onChange={(e) => {
        updateCondition(condition.id, 'customFieldValue', e.target.value);
      }}
      placeholder="Enter custom field name"
      disabled={isLoading}
    />
  ) : (
    <select
      className="form-select"
      value={condition.fieldId}
      onChange={(e) => updateCondition(condition.id, 'fieldId', e.target.value)}
      disabled={isLoading || 
                eventType === 'call_duration' || 
                eventType === 'call_count' || 
                eventType === 'create_quotation' || 
                eventType === 'create_invoice' || 
                eventType === 'create_order' ||
                eventType === 'edit_quotation' ||
                eventType === 'edit_invoice' ||
                eventType === 'edit_order'}
    >
      {/* Existing options */}
      {eventType === 'call_duration' ? (
        <option value="extraDetails.duration">Call Duration</option>
      ) : eventType === 'call_count' ? (
        <option value="count">Call Count</option>
      ) : (eventType === 'create_quotation' || eventType === 'create_invoice' || eventType === 'create_order') ? (
        <option value="total">Total</option>
      ) : (eventType === 'edit_quotation' || eventType === 'edit_invoice' || eventType === 'edit_order') ? (
        <option value="status">Status</option>
      ) : (
        <>
          <option value="">Select Field</option>
          {customFormFields.map((field: any, idx: number) => (
            <option key={idx} value={field.value}>
              {field.name}
            </option>
          ))}
          {/* Add this option for all events that can use custom fields */}
          {(eventType === 'create_lead' || eventType === 'edit_lead' || eventType === 'new_lead_added') && (
            <option value="custom_field_entry">Enter custom field</option>
          )}
        </>
      )}
    </select>
  )}
</div>
            
            {/* Operator Selector */}
            <div className="col-md-3">
              <select
                className="form-select"
                value={condition.operator}
                onChange={(e) => updateCondition(condition.id, 'operator', e.target.value)}
                disabled={isLoading || !condition.fieldId}
              >
                <option value="">Select Operator</option>
                {renderOperators(condition.fieldId)}
              </select>
            </div>
            
            {/* Value Input */}
            <div className="col-md-3">
              {renderValueInput(condition)}
            </div>
            
       {/* Remove Button */}
<div className="col-md-1">
  <button
    type="button"
    className="btn btn-outline-danger btn-sm"
    onClick={() => removeCondition(condition.id)}
    disabled={isLoading}
  >
    <i className="fa fa-times"></i>
  </button>
</div>


            
            {/* Logic Operator (not for last condition) */}
          
{index < conditions.length - 1 && (
  <div className="col-md-2">
    <select
      className="form-select"
      value="AND"  // Force value to always be AND
      disabled={true}  // Disable the dropdown completely
      onChange={(e) => updateLogicOperator(condition.id, e.target.value as 'AND' | 'OR')}
    >
      <option value="AND">AND</option>
      {/* Remove OR option altogether */}
    </select>
  </div>
)}
          </div>
        </div>
      ))}
      


    </div>
  );
};

export default React.memo(AutomationConditionBlock);